import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCookie, faMicrophone } from '@fortawesome/pro-regular-svg-icons';
import cx from 'classnames';

import { useConfigContext } from 'lib/core/config';

import { useRequirementsContext } from 'lib/common/contexts/RequirementsContext';
import type { TBrowserPermissions } from 'lib/common/contexts/RequirementsContext';

import Icon from 'lib/common/components/Icon';
import Text from 'lib/common/components/Text';

import { HELP_LINKS } from 'lib/common/constants/help';

import WarningBanner from '../WarningBanner';

import styles from './requirements-banner.module.scss';

const RequirementItem = ({ icon, label, link }: { icon: IconDefinition; label: string; link: string }) => {
  return (
    <div className={styles['requirement-item']}>
      <Icon icon={icon} size={12} />
      <Text type="helper" bold>
        {label}
      </Text>
      <Text onClick={() => window.open(link)}>Learn More</Text>
    </div>
  );
};

const RequirementsBody = ({
  isPermissionGranted,
}: {
  isPermissionGranted: (permission: TBrowserPermissions) => boolean;
}) => {
  const { config } = useConfigContext();

  return (
    <div>
      <Text className={styles['requirements-body']}>For the best experience, please enable access to:</Text>
      {!isPermissionGranted('microphone') && (
        <RequirementItem
          icon={faMicrophone}
          label="Your Microphone"
          link={`${config.BRAND.helpDocsUrl}${HELP_LINKS.REQUIREMENTS.MICROPHONE_ACCESS}`}
        />
      )}
      {!isPermissionGranted('persistent-storage') && (
        <RequirementItem
          icon={faCookie}
          label="Cookies"
          link={`${config.BRAND.helpDocsUrl}${HELP_LINKS.REQUIREMENTS.STORAGE_ACCESS}`}
        />
      )}
    </div>
  );
};

const RequirementsBanner = ({ className }: { className?: string }) => {
  const { isAccessRequired, isReloadRequired, isPermissionGranted } = useRequirementsContext();

  const title = (
    <Text className={cx(styles['requirements-banner__content__titlebar__title'])} medium>
      Missing Access{' '}
      {isReloadRequired && (
        <Text className={styles['requirements-banner__content__titlebar__subtitle']} medium inline type="small">
          (Reload Required)
        </Text>
      )}
    </Text>
  );

  if (!isAccessRequired) {
    return <></>;
  }

  return (
    <WarningBanner
      className={className}
      title={title}
      body={<RequirementsBody isPermissionGranted={isPermissionGranted} />}
    />
  );
};

export default RequirementsBanner;
